/* ============================================
   AuthGuard — Protects admin routes
   Requires authenticated session with role='admin'
   - Redirige a /login si no hay sesión
   - Redirige al dominio portal (client) si el rol no es admin
   - Redirige a /seleccionar-cuenta si falta elegir cuenta
   ============================================ */

import { Navigate, Outlet } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { crossDomainTarget } from '@/lib/domain'

export default function AuthGuard() {
  const { user, isAuthenticated, role, isCliente, isPartner, needsAccountSelection, mustChangePassword, loading } = useAuth()

  // Separación por dominio: si el rol no corresponde a este dominio, ir al correcto
  // El correo va en el salto: el token no cruza de dominio (localStorage es por
  // origen), asi que del otro lado hay que volver a entrar — al menos con el
  // correo puesto y directo en la contrasena.
  const domainTarget = !loading && isAuthenticated ? crossDomainTarget(role, user?.email) : null
  useEffect(() => { if (domainTarget) window.location.replace(domainTarget) }, [domainTarget])

  if (loading) {
    return (
      <div className="auth-loading">
        <div className="auth-spinner" />
        <p>Verificando sesión...</p>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Dominio equivocado para este rol → redirigiendo (cross-domain)
  if (domainTarget) {
    return (
      <div className="auth-loading">
        <div className="auth-spinner" />
        <p>Redirigiendo...</p>
      </div>
    )
  }

  // Identidad autenticada pero sin cuenta activa → elegir cuenta
  if (needsAccountSelection) {
    return <Navigate to="/seleccionar-cuenta" replace />
  }

  // Cliente users trying to access admin routes → redirect to portal
  if (isCliente) {
    return <Navigate to="/portal" replace />
  }

  // Partner users trying to access admin routes → redirect to partner area
  if (isPartner) {
    return <Navigate to="/partner/pipeline" replace />
  }

  // Force password change on first login
  if (mustChangePassword) {
    return <Navigate to="/cambiar-password" replace />
  }

  return <Outlet />
}
